import * as React from 'react'
import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listAllFacilities, type AdminFacility } from '@/features/admin/api'
import { AddFacilityDrawer } from './AddFacilityDrawer'
import { EditAdminFacilityDrawer } from './EditAdminFacilityDrawer'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { Button } from '@/components/primitives/Button'
import { Pagination } from '@/components/primitives/Pagination'
import { PageLoader } from '@/components/feedback/PageLoader'
import { formatPhoneDisplay } from '@/lib/phone'
import '../ManagePages.css'

function locationLine(f: { city: string | null; state: string | null; postal_code: string | null }): string {
  return [f.city, f.state, f.postal_code].filter(Boolean).join(', ') || '—'
}

const AVATAR_COLORS = ['#4f6ef7', '#0ea5a0', '#7c3aed', '#d97706', '#db2777', '#059669', '#0284c7']
function avatarColor(name: string): string {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) ?? 0)) % AVATAR_COLORS.length
  return AVATAR_COLORS[idx] ?? '#4f6ef7'
}
function facilityInitials(name: string): string {
  return name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase()
}

export function AdminFacilitiesPage(): React.JSX.Element {
  const [addOpen, setAddOpen] = useState(false)
  const [editFacility, setEditFacility] = useState<AdminFacility | null>(null)

  const facilitiesQuery = useQuery({ queryKey: ['admin-facilities'], queryFn: listAllFacilities })
  const allFacilities = facilitiesQuery.data ?? []

  const [search, setSearch]   = useState('')
  const [orgFilter, setOrgFilter] = useState('')
  const [page, setPage]       = useState(1)
  const [perPage, setPerPage] = useState(25)

  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const f of allFacilities) seen.set(f.tenant_id, f.tenant_name)
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [allFacilities])

  const facilities = useMemo(() => {
    let r = [...allFacilities]
    if (orgFilter) r = r.filter(f => f.tenant_id === orgFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      r = r.filter(f =>
        f.name.toLowerCase().includes(q) ||
        f.tenant_name.toLowerCase().includes(q) ||
        (f.city ?? '').toLowerCase().includes(q),
      )
    }
    r.sort((a, b) => a.name.localeCompare(b.name))
    return r
  }, [allFacilities, search, orgFilter])

  useEffect(() => { setPage(1) }, [search, orgFilter])

  const totalPages = Math.max(1, Math.ceil(facilities.length / perPage))
  const pageFacilities = facilities.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-shell-content">
      <AddFacilityDrawer open={addOpen} onClose={() => setAddOpen(false)} />
      <EditAdminFacilityDrawer facility={editFacility} onClose={() => setEditFacility(null)} />
      <main className="manage-main">
        <div className="manage-head-row">
          <div>
            <h1 className="manage-title">Facilities</h1>
            <p className="manage-sub">All physical sites across every organization.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} leftIcon={<PlusIcon />}>Add facility</Button>
        </div>

<div className="manage-card-table">
          <div className="manage-card-table-header">
            <div>
              <div className="manage-card-table-title">All facilities</div>
              {allFacilities.length > 0 && (
                <div className="manage-card-table-sub">{facilities.length} of {allFacilities.length} {allFacilities.length !== 1 ? 'facilities' : 'facility'}</div>
              )}
            </div>
            <div className="manage-toolbar">
              <input
                className="manage-search"
                placeholder="Search by name or city…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select
                className="select manage-filter-select"
                value={orgFilter}
                onChange={e => setOrgFilter(e.target.value)}
              >
                <option value="">All organizations</option>
                {orgOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {facilitiesQuery.isLoading ? (
            <div style={{ padding: 'var(--space-8)' }}><PageLoader /></div>
          ) : allFacilities.length === 0 ? (
            <div className="manage-empty-state">
              <BuildingIcon />
              <p>No facilities yet.</p>
              <Button onClick={() => setAddOpen(true)}>Add the first</Button>
            </div>
          ) : facilities.length === 0 ? (
            <div className="manage-empty-state"><p>No results match your filters.</p></div>
          ) : (
            <table className="manage-table">
              <thead>
                <tr>
                  <th>Facility</th>
                  <th>Organization</th>
                  <th>Location</th>
                  <th>Phone</th>
                  <th className="manage-col-action">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageFacilities.map((f) => {
                  const color = avatarColor(f.name)
                  return (
                    <tr
                      key={f.id}
                      className="is-clickable"
                      onClick={() => setEditFacility(f)}
                    >
                      <td>
                        <div className="admin-cell-with-avatar">
                          <div className="admin-avatar admin-avatar--sq" style={{ background: color }}>
                            {facilityInitials(f.name)}
                          </div>
                          <div className="manage-name">{f.name}</div>
                        </div>
                      </td>
                      <td>
                        <div className="admin-org-pill" style={{ borderColor: avatarColor(f.tenant_name), color: avatarColor(f.tenant_name) }}>
                          {f.tenant_name}
                        </div>
                      </td>
                      <td>
                        <div className="manage-name" style={{ fontWeight: 400 }}>
                          {f.address_line1 ?? ''}
                        </div>
                        <div className="manage-email">{locationLine(f)}</div>
                      </td>
                      <td><span className="manage-email">{formatPhoneDisplay(f.phone)}</span></td>
                      <td className="manage-col-action" onClick={(e) => e.stopPropagation()}>
                        <div className="manage-row-actions">
                          <Button variant="ghost" size="sm" leftIcon={<PencilIcon />} onClick={() => setEditFacility(f)}>
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} totalPages={totalPages} total={facilities.length} perPage={perPage} onPage={setPage} onPerPage={n => { setPerPage(n); setPage(1) }} />
        </div>
      </main>
      </div>
    </div>
  )
}

function PlusIcon(): React.JSX.Element {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
}
function PencilIcon(): React.JSX.Element {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
}
function BuildingIcon(): React.JSX.Element {
  return <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></svg>
}
